import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Grid } from '@mui/material';
import { useFormContext, useWatch } from 'react-hook-form';
import { SublistQuestion } from 'store/types/ApplicationQuestion';
import { SublistAnswer } from 'store/types/ApplicationAnswer';
import Modal from 'components/shared/Modal';
import { ModalProps } from 'store/types/ComponentProps';
import QuestionType from 'store/enums/QuestionType';
import { v4 as uuid } from 'uuid';
import { defaultGridContainerProps, defaultGridItemProps } from 'util/Layout';
import TextQuestionFormItem from '../../TextQuestionFormItem';
import CountryQuestionFormItem from '../CountryQuestionFormItem';
import StateQuestionFormItem from '../StateQuestionFormItem';
import PostalCodeQuestionFormItem from '../PostalCodeQuestionFormItem';

import commonStyles from 'styles/common.module.scss';
import { isCanadaSelected, isUSSelected } from 'util/Form';
import classNames from 'classnames';
import CheckboxQuestionFormItem from '../CheckboxQuestionFormItem';

interface AddAddressItemModalProps extends ModalProps {
  questions: SublistQuestion[];
  fieldName: string;
  parentQuestionType?: QuestionType;
}

const getQuestionInput = (
  fieldName: string,
  { type, options, content, required }: SublistQuestion,
  open: boolean,
  parentFieldName: string,
  countryFieldName?: string
) => {
  const inputProps = {
    fieldName,
    options,
    disabled: !open,
    required: required && open,
  };

  switch (type) {
    case QuestionType.Text:
      return <TextQuestionFormItem {...inputProps} label={content} />;
    case QuestionType.Checkbox:
      return <CheckboxQuestionFormItem {...inputProps} label={content} />;
    case QuestionType.Country:
      return <CountryQuestionFormItem {...inputProps} addressFieldName={parentFieldName} />;
    case QuestionType.State:
      return (
        <StateQuestionFormItem {...inputProps} addressFieldName={parentFieldName} countryFieldName={countryFieldName} />
      );
    case QuestionType.ZipCode:
      return (
        <PostalCodeQuestionFormItem
          {...inputProps}
          addressFieldName={parentFieldName}
          countryFieldName={countryFieldName}
        />
      );
    default:
      return <></>;
  }
};

const getQuestionFieldName = (questionId: string): string => `sublistQuestion-${questionId}`;

const getDefaultViewValues = (questions: SublistQuestion[]) => {
  return {
    ...questions.reduce((acc, { id, type }) => {
      if (type === QuestionType.Checkbox) {
        return { ...acc, [getQuestionFieldName(id)]: false };
      } else if (type === QuestionType.Country) {
        return { ...acc, [getQuestionFieldName(id)]: 'US' };
      } else {
        return { ...acc, [getQuestionFieldName(id)]: '' };
      }
    }, {}),
  };
};

const checkStatesEnabled = (countryValue: string): boolean =>
  isUSSelected(countryValue) || isCanadaSelected(countryValue);

const AddAddressItemModal: React.FunctionComponent<AddAddressItemModalProps> = ({
  onClose,
  open,
  questions,
  fieldName,
}) => {
  const { control, setValue, reset, getValues } = useFormContext();
  const [referenceKey, setReferenceKey] = useState<string>(uuid());
  const controlFieldNameView = useMemo(() => `${fieldName}-view`, [fieldName]);
  const controlFieldNameData = useMemo(() => `${fieldName}-data`, [fieldName]);
  const sublistAnswers: Array<SublistAnswer[]> = useWatch({ control, name: fieldName });
  const sublistAnswersView = useWatch({ control, name: controlFieldNameView, defaultValue: 'US' });
  const countryQuestion: SublistQuestion | undefined = useMemo(
    () => questions.find((question) => question.type === QuestionType.Country),
    [questions]
  );
  const countryFieldName = useMemo(
    () => (countryQuestion ? `${controlFieldNameView}.${getQuestionFieldName(countryQuestion.id)}` : ''),
    [controlFieldNameView, countryQuestion]
  );
  const countryValue: string = useWatch({ control, name: countryFieldName });
  const statesEnabled: boolean = useMemo(() => checkStatesEnabled(countryValue), [countryValue]);
  const addButtonDisabled: boolean = useMemo(
    () =>
      !sublistAnswersView ||
      questions.filter(
        ({ id, required, type }) =>
          required &&
          !sublistAnswersView[getQuestionFieldName(id)] &&
          type !== QuestionType.Checkbox &&
          (type !== QuestionType.State || statesEnabled)
      ).length > 0,
    [questions, statesEnabled, sublistAnswersView]
  );

  const generateNewReferenceKey = useCallback(() => {
    setReferenceKey(uuid());
  }, []);

  const getControlName = useCallback(
    (questionId: string) => `${controlFieldNameView}.${getQuestionFieldName(questionId)}`,
    [controlFieldNameView]
  );

  const handleAddItem = useCallback(() => {
    reset(
      {
        ...getValues(),
        [fieldName]: [...sublistAnswers, Object.values(getValues()[controlFieldNameData])],
        [controlFieldNameView]: getDefaultViewValues(questions),
        [controlFieldNameData]: '',
      },
      { keepDirty: true }
    );
    generateNewReferenceKey();
  }, [
    reset,
    getValues,
    fieldName,
    sublistAnswers,
    controlFieldNameData,
    controlFieldNameView,
    questions,
    generateNewReferenceKey,
  ]);

  const handleAddItemAndClose = useCallback(() => {
    handleAddItem();
    onClose();
  }, [handleAddItem, onClose]);

  const handleClose = useCallback(() => {
    reset(
      {
        ...getValues(),
        [controlFieldNameView]: getDefaultViewValues(questions),
        [controlFieldNameData]: '',
      },
      { keepDirty: true }
    );
    onClose();
  }, [controlFieldNameData, controlFieldNameView, getValues, onClose, questions, reset]);

  useEffect(() => {
    reset({
      ...getValues(),
      [controlFieldNameView]: getDefaultViewValues(questions),
    });
  }, [controlFieldNameData, controlFieldNameView, getValues, questions, reset]);

  useEffect(() => {
    if (!sublistAnswersView) return;
    questions.forEach(({ id, type, content }) => {
      const questionValue = sublistAnswersView[getQuestionFieldName(id)];

      if (questionValue || questionValue === '' || type === QuestionType.Checkbox) {
        setValue(`${controlFieldNameData}.${id}`, {
          answer: String(questionValue),
          sublistQuestionId: id,
          type,
          content,
          referenceKey,
        });
      }
    });
  }, [controlFieldNameData, controlFieldNameView, questions, referenceKey, setValue, sublistAnswersView]);

  return (
    <Modal
      onClose={handleClose}
      open={open}
      title={'Add New Item'}
      actions={
        <Grid {...defaultGridContainerProps}>
          <Grid {...defaultGridItemProps} xs={true}>
            <Button color={'secondary'} variant={'outlined'} onClick={handleClose}>
              {'Cancel'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button color={'secondary'} variant={'contained'} onClick={handleAddItem} disabled={addButtonDisabled}>
              {'Add & Create New'}
            </Button>
          </Grid>
          <Grid {...defaultGridItemProps} xs={'auto'}>
            <Button
              color={'secondary'}
              variant={'contained'}
              onClick={handleAddItemAndClose}
              disabled={addButtonDisabled}
            >
              {'Add'}
            </Button>
          </Grid>
        </Grid>
      }
    >
      <Grid {...defaultGridContainerProps}>
        {questions.map((question) => (
          <Grid
            {...defaultGridItemProps}
            key={question.id}
            className={classNames({
              [commonStyles.hidden]: question.type === QuestionType.State && !statesEnabled,
            })}
          >
            {question.type !== QuestionType.Checkbox && (
              <h5 className={commonStyles.subTitle}>
                {question.content}
                {question.required ? '*' : ''}
              </h5>
            )}
            {getQuestionInput(getControlName(question.id), question, open, controlFieldNameView, countryFieldName)}
          </Grid>
        ))}
      </Grid>
    </Modal>
  );
};
export default AddAddressItemModal;
